import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Send, ArrowLeft, LifeBuoy } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  UniversalLoadingState,
  UniversalEmptyState,
  useToast,
} from '@sighthost/ui';
import { ticketService } from '@/domains/support/services/ticketService';
import {
  ticketAttachmentService,
  type TicketAttachment,
} from '@/domains/support/services/ticketAttachmentService';
import {
  AttachmentChips,
  AttachmentPicker,
} from '@/domains/support/components/TicketAttachments';
import type {
  Ticket,
  TicketMessage,
  TicketStatus,
  TicketPriority,
} from '@/domains/support/types/ticket.types';

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  pending_customer: 'Awaiting your reply',
  closed: 'Closed',
};

const STATUS_TONE: Record<TicketStatus, StatusTone> = {
  open: 'info',
  pending_customer: 'pending',
  closed: 'draft',
};

const PRIORITY_TONE: Record<TicketPriority, StatusTone> = {
  low: 'draft',
  normal: 'info',
  high: 'pending',
  urgent: 'overdue',
};

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [file, setFile] = useState<File | null>(null);

  function refresh() {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      ticketService.getById(id),
      ticketService.listMessages(id),
      ticketAttachmentService.list(id),
    ])
      .then(([t, msgs, atts]) => {
        setTicket(t);
        setMessages(msgs);
        setAttachments(atts);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(refresh, [id]);

  async function submitReply() {
    if (!ticket || !reply.trim()) return;
    setSubmitting(true);
    try {
      const res = await ticketService.reply({ ticket_id: ticket.id, body: reply });
      if (file) {
        try {
          await ticketAttachmentService.attach(ticket.id, file, res.message_id);
        } catch (attachErr) {
          // The reply itself succeeded — surface the attachment failure
          // separately instead of implying the whole reply failed.
          toast({
            tone: 'warning',
            title: 'Reply sent, but the attachment failed',
            body: attachErr instanceof Error ? attachErr.message : String(attachErr),
          });
        }
      }
      toast({
        tone: 'success',
        title: 'Reply sent',
        body: 'Our support team will respond shortly.',
      });
      setReply('');
      setFile(null);
      refresh();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not send reply',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <UniversalLoadingState variant="rows" count={5} />;
  if (error) {
    return (
      <NotificationCard tone="danger" title="Could not load ticket" body={error.message} />
    );
  }
  if (!ticket) {
    return (
      <UniversalEmptyState
        icon={LifeBuoy}
        title="Ticket not found"
        description="It may have been closed or removed."
      />
    );
  }

  return (
    <UniversalPage
      title={`${ticket.number} — ${ticket.subject}`}
      description="Conversation with SightHost support."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Support', href: '/app/support' },
        { label: ticket.number },
      ]}
      actions={
        <Button variant="outline" size="sm" onClick={() => navigate('/app/support')}>
          <ArrowLeft className="h-4 w-4" />
          Back to tickets
        </Button>
      }
    >
      <Card interactive={false}>
        <header className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
          <StatusBadge tone={STATUS_TONE[ticket.status]} dot>
            {STATUS_LABEL[ticket.status]}
          </StatusBadge>
          <StatusBadge tone={PRIORITY_TONE[ticket.priority]}>
            {ticket.priority}
          </StatusBadge>
          <span className="ml-auto text-xs text-muted-foreground">
            Opened {new Date(ticket.openedAt).toLocaleString('en-ZA')}
          </span>
        </header>

        {messages.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">
            No messages yet.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {messages.map((m) => (
              <article
                key={m.id}
                className={
                  'px-6 py-4 ' +
                  (m.authorKind === 'customer' ? 'bg-warning/10/30' : 'bg-muted/30')
                }
              >
                <header className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium uppercase tracking-wider text-foreground">
                    {m.authorKind === 'customer' ? 'You' : 'SightHost Support'}
                  </span>
                  <span className="ml-auto tabular-nums">
                    {new Date(m.createdAt).toLocaleString('en-ZA')}
                  </span>
                </header>
                <div className="whitespace-pre-wrap text-sm text-foreground">
                  {m.body}
                </div>
                <AttachmentChips
                  attachments={attachments.filter((a) => a.messageId === m.id)}
                />
              </article>
            ))}
          </div>
        )}
        {attachments.some((a) => a.messageId === null) && (
          <div className="border-t border-border px-6 py-3">
            <p className="text-xs font-medium text-muted-foreground">Ticket attachments</p>
            <AttachmentChips attachments={attachments.filter((a) => a.messageId === null)} />
          </div>
        )}
      </Card>

      {ticket.status !== 'closed' ? (
        <Card interactive={false}>
          <header className="border-b border-border px-6 py-3">
            <h3 className="text-sm font-semibold">Reply</h3>
          </header>
          <div className="space-y-3 px-6 py-4">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              disabled={submitting}
              placeholder="Add more details, ask a follow-up, or confirm we've sorted it."
              className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              maxLength={10000}
            />
            <div className="flex flex-wrap items-end justify-between gap-3">
              <AttachmentPicker file={file} onFileChange={setFile} disabled={submitting} />
              <Button
                onClick={submitReply}
                disabled={submitting || !reply.trim()}
                loading={submitting}
              >
                <Send className="h-4 w-4" />
                Send reply
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card interactive={false} className="bg-muted/30 px-6 py-4">
          <p className="text-sm text-muted-foreground">
            This ticket is closed. If your issue isn't fully resolved, open a
            new ticket and reference <code className="font-sans">{ticket.number}</code>.
          </p>
        </Card>
      )}
    </UniversalPage>
  );
}
